import { getLocalName, numericSort } from 'vet-bones/utils';
import {
  ModelingAndMappingAction,
  ModelingAndMappingActionType,
} from 'vet-bones/utils/voicebox';

import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import {
  ModelConceptAttribute,
  ModelConceptClass,
  ModelConceptRelationship,
} from 'src/containers/ProjectDiagram/types/data';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import {
  LinkData,
  Model,
  NodeData,
} from 'src/containers/ProjectDiagram/types/model';
import { addDeletedData } from 'src/containers/ProjectDiagram/utils/addDeletedData';
import {
  isModelConceptClass,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import { setDescriptionForIri } from 'src/containers/ProjectDiagram/utils/descriptions';
import { setLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { addAttribute } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addAttribute';
import { addDomainToAttributeModelData } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addDomainToAttributeModelData';
import { removeModelConceptAttribute } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/removeModelConceptAttribute';
import { setDomainsForAttributeModelData } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/setDomainsForAttributeModelData';
import { setRangesForAttributeModelData } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/setRangesForAttributeModelData';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { removeModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/removeModelConceptClass';
import { addDomainOrRangeToRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addDomainOrRangeToRelationship';
import { addUnconnectedModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addUnconnectedModelConceptRelationship';
import { getRelationshipDomainsAndRanges } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/getRelationshipDomainsAndRanges';
import { removeModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/removeModelConceptRelationship';
import { removeModelConceptRelationshipForClass } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/removeModelConceptRelationshipForClass';
import { addProjectModelIdsToIri } from 'src/containers/ProjectDiagram/utils/projectModelIds';
import { setPropertyType } from 'src/containers/ProjectDiagram/utils/propertyType/setPropertyType';

const ACTION_TYPE_SORT_ORDER = [
  ModelingAndMappingActionType.DELETE_MODEL_CONCEPT_CLASS,
  ModelingAndMappingActionType.DELETE_MODEL_CONCEPT_RELATIONSHIP,
  ModelingAndMappingActionType.DELETE_MODEL_CONCEPT_ATTRIBUTE,
  ModelingAndMappingActionType.CREATE_MODEL_CONCEPT_CLASS,
  ModelingAndMappingActionType.CREATE_MODEL_CONCEPT_RELATIONSHIP,
  ModelingAndMappingActionType.CREATE_MODEL_CONCEPT_ATTRIBUTE,
  ModelingAndMappingActionType.UPDATE_MODEL_CONCEPT_CLASS,
  ModelingAndMappingActionType.UPDATE_MODEL_CONCEPT_RELATIONSHIP,
  ModelingAndMappingActionType.UPDATE_MODEL_CONCEPT_ATTRIBUTE,
];
const actionsSorter = numericSort((action: ModelingAndMappingAction) =>
  ACTION_TYPE_SORT_ORDER.indexOf(action.type)
);

export const applyVoiceboxChanges =
  (
    actions: ModelingAndMappingAction[],
    projectModelIds: string[],
    addedOrUpdatedIris: Set<string>,
    removedIris: Set<string>
  ) =>
  (model: Model, diagram: Diagram) => {
    const updateLabelAndDescription = (action: ModelingAndMappingAction) => {
      if (action.data.label !== undefined) {
        setLabelForIri(action.data.iri, action.data.label)(model);
      }
      if (action.data.description !== undefined) {
        setDescriptionForIri(action.data.iri, action.data.description)(model);
      }
    };

    const existingClass = (iri: string) =>
      model.nodeDataArray.find(
        (nodeData): nodeData is ModelConceptClass =>
          isModelConceptClass(nodeData) && nodeData.value === iri
      );

    const existingRelationship = (iri: string) =>
      model.linkDataArray.find(
        (linkData): linkData is ModelConceptRelationship =>
          isModelConceptRelationship(linkData) && linkData.value === iri
      );

    const existingAttribute = (iri: string): ModelConceptAttribute | null =>
      model.modelData.attributesByIri[iri] || null;

    const addNewClass = (iri: string, label?: string, description?: string) => {
      addedOrUpdatedIris.add(iri);
      addModelConceptClass({
        iri,
        label: label || getLocalName(iri),
        description,
        projectModelIds,
      })(model);
    };

    const addNewRelationship = (
      iri: string,
      label?: string,
      description?: string,
      domains?: string[],
      ranges?: string[]
    ) => {
      addedOrUpdatedIris.add(iri);
      if (domains || ranges) {
        domains?.forEach((domain) => {
          if (!existingClass(domain)) {
            addNewClass(domain);
          }
          addDomainOrRangeToRelationship({
            direction: RelationshipDirection.OUTGOING,
            relationshipIri: iri,
            classIri: domain,
            copyModelIds: true,
            useAllValidLinks: true,
          })(model, diagram);
        });
        ranges?.forEach((range) => {
          if (!existingClass(range)) {
            addNewClass(range);
          }
          addDomainOrRangeToRelationship({
            direction: RelationshipDirection.INCOMING,
            relationshipIri: iri,
            classIri: range,
            copyModelIds: true,
            useAllValidLinks: true,
          })(model, diagram);
        });
      } else {
        addUnconnectedModelConceptRelationship({
          value: iri,
        })(diagram.model, diagram);
        addProjectModelIdsToIri(iri, projectModelIds)(model);
      }
      setLabelForIri(iri, label || getLocalName(iri))(diagram.model);
      setDescriptionForIri(iri, description || '')(diagram.model);
      setPropertyType(iri)(diagram.model);
    };

    const updateRelationshipDomainsAndRanges = (
      iri: string,
      domains?: string[],
      ranges?: string[]
    ) => {
      if (domains || ranges) {
        addedOrUpdatedIris.add(iri);
        const nextDomains = domains && new Set(domains);
        const nextRanges = ranges && new Set(ranges);
        const { domains: existingDomains, ranges: existingRanges } =
          getRelationshipDomainsAndRanges(model.linkDataArray, iri);
        existingDomains.forEach((domain) => {
          if (nextDomains && !nextDomains.has(domain)) {
            removeModelConceptRelationshipForClass({
              classIri: domain,
              direction: RelationshipDirection.OUTGOING,
              iri,
            })(model);
          }
        });
        nextDomains?.forEach((domain) => {
          if (!existingDomains.has(domain)) {
            if (!existingClass(domain)) {
              console.warn(`Concept ${domain} does not exist`);
              addNewClass(domain);
            }
            addDomainOrRangeToRelationship({
              classIri: domain,
              direction: RelationshipDirection.OUTGOING,
              relationshipIri: iri,
              useAllValidLinks: true,
            })(model, diagram);
          }
        });
        existingRanges.forEach((range) => {
          if (nextRanges && !nextRanges.has(range)) {
            removeModelConceptRelationshipForClass({
              classIri: range,
              direction: RelationshipDirection.INCOMING,
              iri,
            })(model);
          }
        });
        nextRanges?.forEach((range) => {
          if (!existingRanges.has(range)) {
            if (!existingClass(range)) {
              console.warn(`Concept ${range} does not exist`);
              addNewClass(range);
            }
            addDomainOrRangeToRelationship({
              classIri: range,
              direction: RelationshipDirection.INCOMING,
              relationshipIri: iri,
              useAllValidLinks: true,
            })(model, diagram);
          }
        });
      }
    };

    const addNewAttribute = (
      iri: string,
      label?: string,
      description?: string,
      domains?: string[],
      ranges?: string[]
    ) => {
      addedOrUpdatedIris.add(iri);
      addAttribute({
        value: iri,
        text: label || getLocalName(iri),
        ranges: (ranges || []).map((value) => ({
          value,
        })),
        projectModelIds,
      })(model);
      domains?.forEach((domain) => {
        if (!existingClass(domain)) {
          console.warn(`Concept ${domain} does not exist`);
          addNewClass(domain);
        }
        addDomainToAttributeModelData({
          value: iri,
          domain,
        })(model);
      });
      setDescriptionForIri(iri, description || '')(diagram.model);
    };

    const updateAttributeDomainsAndRanges = (
      iri: string,
      domains?: string[],
      ranges?: string[]
    ) => {
      if (domains) {
        addedOrUpdatedIris.add(iri);
        domains.forEach((domain) => {
          if (!existingClass(domain)) {
            console.warn(`Concept ${domain} does not exist`);
            addNewClass(domain);
          }
        });
        setDomainsForAttributeModelData(iri, domains)(model);
      }
      if (ranges) {
        addedOrUpdatedIris.add(iri);
        setRangesForAttributeModelData(iri, ranges)(model);
      }
    };

    const deletedClassIris = new Set<string>();
    const deletedRelationshipIris = new Set<string>();

    const sortedActions = actions.sort(actionsSorter);
    sortedActions.forEach((action) => {
      if (
        action.type === ModelingAndMappingActionType.DELETE_MODEL_CONCEPT_CLASS
      ) {
        deletedClassIris.add(action.data.iri);
      } else if (
        action.type ===
        ModelingAndMappingActionType.DELETE_MODEL_CONCEPT_RELATIONSHIP
      ) {
        deletedRelationshipIris.add(action.data.iri);
      }
    });

    const deletedDataArray: Array<NodeData | LinkData> = [];
    if (deletedClassIris.size) {
      model.nodeDataArray.forEach((nodeData) => {
        if (
          isModelConceptClass(nodeData) &&
          deletedClassIris.has(nodeData.value)
        ) {
          deletedDataArray.push(nodeData);
        }
      });
    }
    if (deletedRelationshipIris.size) {
      model.linkDataArray.forEach((linkData) => {
        if (
          isModelConceptRelationship(linkData) &&
          deletedRelationshipIris.has(linkData.value)
        ) {
          deletedDataArray.push(linkData);
        }
      });
    }
    addDeletedData(deletedDataArray)(model);

    sortedActions.forEach((action) => {
      switch (action.type) {
        case ModelingAndMappingActionType.CREATE_MODEL_CONCEPT_CLASS: {
          if (existingClass(action.data.iri)) {
            console.warn(`Concept ${action.data.iri} already exists`);
            break;
          }

          addNewClass(
            action.data.iri,
            action.data.label,
            action.data.description
          );
          break;
        }

        case ModelingAndMappingActionType.CREATE_MODEL_CONCEPT_RELATIONSHIP: {
          if (existingRelationship(action.data.iri)) {
            console.warn(`Concept ${action.data.iri} already exists`);
            updateRelationshipDomainsAndRanges(
              action.data.iri,
              action.data.domains,
              action.data.ranges
            );
            break;
          }

          addNewRelationship(
            action.data.iri,
            action.data.label,
            action.data.description,
            action.data.domains,
            action.data.ranges
          );
          break;
        }

        case ModelingAndMappingActionType.CREATE_MODEL_CONCEPT_ATTRIBUTE: {
          if (existingAttribute(action.data.iri)) {
            console.warn(`Concept ${action.data.iri} already exists`);
            updateAttributeDomainsAndRanges(
              action.data.iri,
              action.data.domains,
              action.data.ranges
            );
            break;
          }

          addNewAttribute(
            action.data.iri,
            action.data.label,
            action.data.description,
            action.data.domains,
            action.data.ranges
          );
          break;
        }

        case ModelingAndMappingActionType.UPDATE_MODEL_CONCEPT_CLASS: {
          if (!existingClass(action.data.iri)) {
            console.warn(`Concept ${action.data.iri} does not exist`);
            addNewClass(
              action.data.iri,
              action.data.label,
              action.data.description
            );
            break;
          }

          addedOrUpdatedIris.add(action.data.iri);
          updateLabelAndDescription(action);
          break;
        }

        case ModelingAndMappingActionType.UPDATE_MODEL_CONCEPT_RELATIONSHIP: {
          if (!existingRelationship(action.data.iri)) {
            console.warn(`Concept ${action.data.iri} does not exist`);
            addNewRelationship(
              action.data.iri,
              action.data.label,
              action.data.description,
              action.data.domains,
              action.data.ranges
            );
            break;
          }

          addedOrUpdatedIris.add(action.data.iri);
          updateLabelAndDescription(action);
          updateRelationshipDomainsAndRanges(
            action.data.iri,
            action.data.domains,
            action.data.ranges
          );
          break;
        }

        case ModelingAndMappingActionType.UPDATE_MODEL_CONCEPT_ATTRIBUTE: {
          if (!existingAttribute(action.data.iri)) {
            console.warn(`Concept ${action.data.iri} does not exist`);
            addNewAttribute(
              action.data.iri,
              action.data.label,
              action.data.description,
              action.data.domains,
              action.data.ranges
            );
            break;
          }

          addedOrUpdatedIris.add(action.data.iri);
          updateLabelAndDescription(action);
          updateAttributeDomainsAndRanges(
            action.data.iri,
            action.data.domains,
            action.data.ranges
          );
          break;
        }

        case ModelingAndMappingActionType.DELETE_MODEL_CONCEPT_CLASS: {
          const nodeData = existingClass(action.data.iri);
          if (!nodeData) {
            console.warn(`Concept ${action.data.iri} does not exist`);
            break;
          }

          removedIris.add(action.data.iri);
          removeModelConceptClass(nodeData)(model);
          break;
        }

        case ModelingAndMappingActionType.DELETE_MODEL_CONCEPT_RELATIONSHIP: {
          const linkData = existingRelationship(action.data.iri);
          if (!linkData) {
            console.warn(`Concept ${action.data.iri} does not exist`);
            break;
          }

          removedIris.add(action.data.iri);
          removeModelConceptRelationship(linkData, true)(model);
          break;
        }

        case ModelingAndMappingActionType.DELETE_MODEL_CONCEPT_ATTRIBUTE: {
          if (!existingAttribute(action.data.iri)) {
            console.warn(`Concept ${action.data.iri} does not exist`);
            break;
          }

          removedIris.add(action.data.iri);
          removeModelConceptAttribute({
            value: action.data.iri,
          })(model);
          break;
        }

        default:
          break;
      }
    });
  };
